import { useFragment } from "@/generated";
import { SlideByIdDocument, SlideFieldsFragmentDoc } from "@/generated/graphql";
import { useQuery } from "@apollo/client/react";

interface UseSlideByIdOptions {
  slideId: string;
  skip?: boolean;
}

/**
 * Fetches a single slide by its unique identifier.
 *
 * Queries the GraphQL API for a specific slide's complete data. Query can be
 * skipped with the skip option when the slideId is not yet available.
 *
 * @param options - Configuration options for the hook
 * @param options.slideId - The unique identifier of the slide to fetch
 * @param options.skip - If true, skips the query execution (default: false)
 * @returns An object containing:
 *   - slide: The slide data object (undefined if loading or not found)
 *   - loading: Boolean indicating if the query is in progress
 *   - error: ApolloError if the query failed, undefined otherwise
 *
 * @example
 * const { slide, loading, error } = useSlideById({ slideId: "slide123" });
 */
export function useSlideById({ slideId, skip = false }: UseSlideByIdOptions) {
  const { data, loading, error } = useQuery(SlideByIdDocument, {
    variables: {
      id: slideId,
    },
    fetchPolicy: "cache-first",
    skip: skip || !slideId,
  });

  const slide = data?.getSlide ? useFragment(SlideFieldsFragmentDoc, data.getSlide) : undefined;

  return {
    slide,
    loading,
    error,
  };
}
